import { useMemo, type CSSProperties } from 'react';
import type { WidgetPosition } from '../types';
import { useMobileViewport } from './useMobileViewport';

/** Placement and visual-viewport sizing, shared by the launcher and open panel. */
export function useWidgetLayout({
  isMobile,
  position,
  fullscreen,
  zIndex,
}: {
  isMobile: boolean;
  position: WidgetPosition;
  fullscreen: boolean;
  zIndex: number;
}): CSSProperties {
  const mobileViewportStyle = useMobileViewport(isMobile);
  const widgetPositionStyle = useMemo((): CSSProperties => {
    const style: CSSProperties = { position: 'fixed' };
    const bottomInset = isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' : '1rem';
    const topInset = isMobile ? 'calc(env(safe-area-inset-top, 0px) + 1rem)' : '1rem';
    const leftInset = isMobile ? 'calc(env(safe-area-inset-left, 0px) + 1rem)' : '1rem';
    const rightInset = isMobile ? 'calc(env(safe-area-inset-right, 0px) + 1rem)' : '1rem';
    if (fullscreen) {
      style.top = 'var(--rw-mobile-offset-top, 0px)';
      style.left = 'var(--rw-mobile-offset-left, 0px)';
      style.width = 'var(--rw-mobile-viewport-width, 100vw)';
      style.height = 'var(--rw-mobile-viewport-height, 100dvh)';
      return style;
    }
    switch (position) {
      case 'bottom-left':
        style.bottom = bottomInset;
        style.left = leftInset;
        break;
      case 'top-left':
        style.top = topInset;
        style.left = leftInset;
        break;
      case 'top-right':
        style.top = topInset;
        style.right = rightInset;
        break;
      case 'bottom-right':
      default:
        style.bottom = bottomInset;
        style.right = rightInset;
        break;
    }
    return style;
  }, [isMobile, position, fullscreen]);

  const widgetStyle = useMemo<CSSProperties>(
    () => ({ zIndex, overflowX: 'hidden', ...widgetPositionStyle, ...mobileViewportStyle }),
    [mobileViewportStyle, widgetPositionStyle, zIndex]
  );

  return widgetStyle;
}
